import { useId, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { UserPlus, Sparkles } from 'lucide-react'
import { PageShell } from '@/components/common/PageShell'
import { WorkspaceToolbar } from '@/components/common/WorkspaceToolbar'
import { Card, Button } from '@/components/ui'
import { CreateUserForm } from './CreateUserForm'
import { InvitationSentModal } from './InvitationSentModal'

export function CreateUserPage(): React.ReactElement {
  const navigate = useNavigate()
  const formId = useId()
  const [invitedEmail, setInvitedEmail] = useState<string | null>(null)
  const [isCreating, setIsCreating] = useState(false)

  const goToList = (): void => {
    navigate('/users')
  }

  return (
    <PageShell
      breadcrumbItems={[
        { label: 'Organization', to: '/dashboard' },
        { label: 'Users', to: '/users' },
        { label: 'New user' },
      ]}
      icon={UserPlus}
      title="Create user"
      description="Add a new user to the organization and send them a secure password setup email."
      className="gap-0"
      hideHeader
    >
      <WorkspaceToolbar
        actions={
          <Button type="button" variant="ghost" size="sm" onClick={goToList}>
            Cancel
          </Button>
        }
      />

      <div className="mt-4 grid grid-cols-1 gap-6 pb-24 lg:grid-cols-3 lg:pb-0">
        <div className="space-y-6 lg:col-span-2">
          <Card contentClassName="p-6">
            <CreateUserForm
              onCancel={goToList}
              externalFormId={formId}
              hideFooter
              onPendingChange={setIsCreating}
              onSuccess={(_userId, email) => {
                setInvitedEmail(email)
              }}
            />
          </Card>
        </div>

        <aside className="hidden lg:col-span-1 lg:block">
          <div className="sticky top-4 space-y-4">
            <Card
              header={
                <div className="flex items-center gap-3">
                  <span className="flex h-9 w-9 items-center justify-center rounded-lg bg-primary/10 text-primary">
                    <Sparkles className="h-5 w-5" aria-hidden />
                  </span>
                  <div>
                    <h2 className="text-sm font-semibold text-gray-900 dark:text-gray-100">
                      Ready to create
                    </h2>
                    <p className="text-xs text-gray-500 dark:text-gray-400">
                      The new user will receive a password setup email right away.
                    </p>
                  </div>
                </div>
              }
              contentClassName="p-5 space-y-2"
            >
              <Button
                form={formId}
                type="submit"
                variant="primary"
                loading={isCreating}
                className="w-full justify-center gap-2"
              >
                Create user
              </Button>
              <Button
                type="button"
                variant="ghost"
                onClick={goToList}
                disabled={isCreating}
                className="w-full justify-center"
              >
                Cancel
              </Button>
            </Card>
          </div>
        </aside>
      </div>

      <div className="fixed inset-x-0 bottom-0 z-30 border-t border-gray-200 bg-white/90 backdrop-blur dark:border-gray-700 dark:bg-gray-900/90 lg:hidden">
        <div className="flex items-center justify-end gap-2 px-4 py-3">
          <Button
            type="button"
            variant="ghost"
            size="sm"
            onClick={goToList}
            disabled={isCreating}
          >
            Cancel
          </Button>
          <Button
            form={formId}
            type="submit"
            variant="primary"
            size="sm"
            loading={isCreating}
            className="gap-2"
          >
            Create user
          </Button>
        </div>
      </div>

      {invitedEmail && (
        <InvitationSentModal
          open={!!invitedEmail}
          email={invitedEmail}
          onClose={() => {
            setInvitedEmail(null)
            goToList()
          }}
        />
      )}
    </PageShell>
  )
}

CreateUserPage.displayName = 'CreateUserPage'
